import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import CustomTypography from '../../components/customTypography/CustomTypography';
import CustomButton from '../../components/customButton/CustomButton';
import CustomField from '../../components/customField/CustomField';
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  CircularProgress, 
  Alert, 
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent
} from '@mui/material';

import { ReactComponent as ArrowUp } from '../../assets/arrowUpIcon.svg';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { 
  fetchAllInvitations, 
  exportInvitationsCSV, 
  InvitationRecipient,
  InvitationsFilters,
  fetchExhibitors,
  fetchExhibitions,
  Exhibitor,
  Exhibition
} from '../../services/api';

const InvitationsTab: React.FC = () => {
  const [invitations, setInvitations] = useState<InvitationRecipient[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Summary stats
  const [summary, setSummary] = useState({
    totalInvitations: 0,
    sent: 0,
    opened: 0,
    accepted: 0,
    pending: 0,
    uniqueExhibitors: 0,
    uniqueExhibitions: 0
  });

  // Filters
  const [search, setSearch] = useState<string>('');
  const [selectedExhibitionId, setSelectedExhibitionId] = useState<string>('');
  const [selectedExhibitorId, setSelectedExhibitorId] = useState<string>('');
  
  const [exhibitors, setExhibitors] = useState<Exhibitor[]>([]);
  const [exhibitions, setExhibitions] = useState<Exhibition[]>([]);
  
  const { token } = useAuth();

  // Load invitations with filters
  const loadInvitations = useCallback(async (): Promise<void> => {
    if (!token) {
      setError('Brak autoryzacji. Proszę się zalogować.');
      return;
    }

    try {
      setLoading(true);
      const filters: InvitationsFilters = {
        sortBy: 'sent_at',
        sortOrder: sortOrder
      };

      if (selectedExhibitionId) filters.exhibitionId = selectedExhibitionId;
      if (selectedExhibitorId) filters.exhibitorId = selectedExhibitorId;
      if (search && search.trim()) filters.search = search.trim();

      const response = await fetchAllInvitations(token, filters);
      setInvitations(response.data);
      setSummary(response.summary);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Nie udało się pobrać zaproszeń');
    } finally {
      setLoading(false);
    }
  }, [token, selectedExhibitionId, selectedExhibitorId, search, sortOrder]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  // Load filter datasets
  useEffect(() => {
    const loadFilterData = async () => {
      if (!token) return;
      try {
        const [exhibitorsData, exhibitionsData] = await Promise.all([
          fetchExhibitors(token),
          fetchExhibitions(token)
        ]);
        setExhibitors(exhibitorsData);
        setExhibitions(exhibitionsData);
      } catch (err) {
        console.error('Error loading filter data:', err);
      }
    };
    loadFilterData();
  }, [token]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleExportCSV = () => {
    if (!token) return;
    
    const filters: InvitationsFilters = {};
    if (selectedExhibitionId) filters.exhibitionId = selectedExhibitionId;
    if (selectedExhibitorId) filters.exhibitorId = selectedExhibitorId;
    if (search && search.trim()) filters.search = search.trim();

    exportInvitationsCSV(token, filters);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Paginated data
  const paginatedInvitations = useMemo(() => {
    return invitations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [invitations, page, rowsPerPage]);

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Summary Statistics */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
        gap: 2, 
        mb: 3 
      }}>
        <Card>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <CustomTypography sx={{ fontSize: '0.85rem', color: '#666' }}>
              Wysłane
            </CustomTypography>
            <CustomTypography sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 'bold', color: '#4caf50' }}>
              {summary.sent}
            </CustomTypography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <CustomTypography sx={{ fontSize: '0.85rem', color: '#666' }}>
              Wystawcy
            </CustomTypography>
            <CustomTypography sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 'bold' }}>
              {summary.uniqueExhibitors}
            </CustomTypography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <CustomTypography sx={{ fontSize: '0.85rem', color: '#666' }}>
              Wydarzenia
            </CustomTypography>
            <CustomTypography sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 'bold' }}>
              {summary.uniqueExhibitions}
            </CustomTypography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        gap: 2, 
        flexWrap: 'wrap', 
        alignItems: 'flex-end',
        '& > *': { 
          minWidth: { xs: '100%', sm: '200px', md: '150px' },
          flex: { xs: '1 1 100%', sm: '1 1 auto' }
        }
      }}>
        <Box sx={{ minWidth: { xs: '100%', sm: '250px' } }}>
          <CustomField
            label="Szukaj"
            placeholder="Email, nazwa, firma..."
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>

        <FormControl>
          <InputLabel>Wydarzenie</InputLabel>
          <Select
            value={selectedExhibitionId}
            onChange={(e) => setSelectedExhibitionId(e.target.value)}
            label="Wydarzenie"
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {exhibitions.map((ex) => (
              <MenuItem key={ex.id} value={ex.id}>
                {ex.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <InputLabel>Wystawca</InputLabel>
          <Select
            value={selectedExhibitorId}
            onChange={(e) => setSelectedExhibitorId(e.target.value)}
            label="Wystawca"
          >
            <MenuItem value="">Wszyscy</MenuItem>
            {exhibitors.map((exhibitor) => (
              <MenuItem key={exhibitor.id} value={exhibitor.id}>
                {exhibitor.companyName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <CustomButton
          onClick={handleExportCSV}
          icon={<FileDownloadIcon style={{ width: '1.2em', height: '1.2em' }} />}
          iconPosition="left"
          variant="contained"
          disabled={invitations.length === 0}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          Eksportuj CSV
        </CustomButton>
      </Box>

      {/* Table */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: '800px', md: '100%' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleSort}>
                      Data wysłania
                      <ArrowUp
                        style={{
                          marginLeft: 8,
                          width: '1em',
                          height: '1em',
                          transform: sortOrder === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)',
                          transition: 'transform 0.3s'
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>Odbiorca</TableCell>
                  <TableCell>Email odbiorcy</TableCell>
                  <TableCell>Firma odbiorcy</TableCell>
                  <TableCell>Wystawca</TableCell>
                  <TableCell>Wydarzenie</TableCell>
                  <TableCell>Szablon</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedInvitations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CustomTypography sx={{ py: 4 }}>
                        Brak zaproszeń do wyświetlenia
                      </CustomTypography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedInvitations.map((invitation) => (
                    <TableRow key={invitation.id} hover>
                      <TableCell>{invitation.id}</TableCell>
                      <TableCell>{formatDate(invitation.sent_at)}</TableCell>
                      <TableCell>{invitation.recipient_name || '-'}</TableCell>
                      <TableCell>{invitation.recipient_email}</TableCell>
                      <TableCell>{invitation.recipient_company || '-'}</TableCell>
                      <TableCell>{invitation.company_name || '-'}</TableCell>
                      <TableCell>{invitation.exhibition_name || '-'}</TableCell>
                      <TableCell>{invitation.template_title || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={invitations.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Wierszy na stronę:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} z ${count}`}
          />
        </>
      )}
    </Box>
  );
};

export default InvitationsTab;

